import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Customer } from '../../types/customer';

export type DuplicateField = 'email' | 'phone' | 'taxNumber' | 'companyName' | 'fullName';

export interface DuplicateMatch {
  id: string;
  field: DuplicateField;
  value: string;
  customers: Customer[];
  confidence: 'high' | 'medium' | 'low';
}

export interface DuplicateScanResult {
  duplicates: DuplicateMatch[];
  totalDuplicates: number;
  totalCustomers: number;
  scannedAt: Date;
}

const COLLECTION = 'customers';

function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

function normalizePhone(phone: string): string {
  return phone.replace(/[\s\-\(\)]/g, '').replace(/^00/, '+');
}

function normalizeName(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, ' ');
}

export const duplicateService = {
  async checkSingle(
    field: DuplicateField,
    value: string,
    organizationId: string,
    excludeId?: string
  ): Promise<Customer[]> {
    if (!value || value.trim() === '') return [];

    let normalizedValue = value;
    if (field === 'email') normalizedValue = normalizeEmail(value);
    if (field === 'phone') normalizedValue = normalizePhone(value);
    if (field === 'companyName' || field === 'fullName') normalizedValue = normalizeName(value);

    const q = query(
      collection(db, COLLECTION),
      where('organizationId', '==', organizationId),
      where(field, '==', normalizedValue)
    );

    const snapshot = await getDocs(q);
    let results = snapshot.docs
      .map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate(), updatedAt: d.data().updatedAt?.toDate() }) as Customer)
      .filter(c => c.id !== excludeId);

    return results;
  },

  async checkMultiple(
    data: { email?: string; phone?: string; taxNumber?: string; companyName?: string; firstName?: string; lastName?: string },
    organizationId: string,
    excludeId?: string
  ): Promise<DuplicateMatch[]> {
    const matches: DuplicateMatch[] = [];
    const fields: { field: DuplicateField; value: string }[] = [];

    if (data.email) fields.push({ field: 'email', value: data.email });
    if (data.phone) fields.push({ field: 'phone', value: data.phone });
    if (data.taxNumber) fields.push({ field: 'taxNumber', value: data.taxNumber });
    if (data.companyName) fields.push({ field: 'companyName', value: data.companyName });
    if (data.firstName && data.lastName) fields.push({ field: 'fullName', value: `${data.firstName} ${data.lastName}` });

    for (const { field, value } of fields) {
      const customers = await this.checkSingle(field, value, organizationId, excludeId);
      if (customers.length > 0) {
        const confidence = field === 'email' || field === 'taxNumber' ? 'high' : field === 'phone' ? 'medium' : 'low';
        matches.push({ id: `${field}_${value}`, field, value, customers, confidence });
      }
    }

    return matches;
  },

  async scanAll(organizationId: string): Promise<DuplicateScanResult> {
    const q = query(
      collection(db, COLLECTION),
      where('organizationId', '==', organizationId)
    );
    const snapshot = await getDocs(q);
    const allCustomers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as Customer);

    const duplicates: DuplicateMatch[] = [];
    const seenGroups = new Set<string>();

    // Group by email
    const byEmail = new Map<string, Customer[]>();
    const byPhone = new Map<string, Customer[]>();
    const byTax = new Map<string, Customer[]>();
    const byCompany = new Map<string, Customer[]>();

    for (const c of allCustomers) {
      if (c.email) {
        const key = normalizeEmail(c.email);
        if (!byEmail.has(key)) byEmail.set(key, []);
        byEmail.get(key)!.push(c);
      }
      if (c.phone) {
        const key = normalizePhone(c.phone);
        if (!byPhone.has(key)) byPhone.set(key, []);
        byPhone.get(key)!.push(c);
      }
      if (c.taxNumber) {
        const key = c.taxNumber.trim();
        if (!byTax.has(key)) byTax.set(key, []);
        byTax.get(key)!.push(c);
      }
      if (c.companyName) {
        const key = normalizeName(c.companyName);
        if (!byCompany.has(key)) byCompany.set(key, []);
        byCompany.get(key)!.push(c);
      }
    }

    const addGroup = (field: DuplicateField, value: string, customers: Customer[], confidence: 'high' | 'medium' | 'low') => {
      if (customers.length < 2) return;
      const groupId = `${field}_${value}`;
      if (seenGroups.has(groupId)) return;
      seenGroups.add(groupId);
      duplicates.push({ id: groupId, field, value, customers, confidence });
    };

    byEmail.forEach((customers, key) => addGroup('email', key, customers, 'high'));
    byTax.forEach((customers, key) => addGroup('taxNumber', key, customers, 'high'));
    byPhone.forEach((customers, key) => addGroup('phone', key, customers, 'medium'));
    byCompany.forEach((customers, key) => addGroup('companyName', key, customers, 'low'));

    return {
      duplicates,
      totalDuplicates: duplicates.reduce((sum, d) => sum + d.customers.length, 0),
      totalCustomers: allCustomers.length,
      scannedAt: new Date(),
    };
  },

  async merge(primaryId: string, secondaryId: string, mergedData: Partial<Customer>): Promise<void> {
    // Update primary with merged data
    await updateDoc(doc(db, COLLECTION, primaryId), {
      ...mergedData,
      updatedAt: serverTimestamp(),
    });

    // Delete secondary
    await deleteDoc(doc(db, COLLECTION, secondaryId));
  },

  async ignoreDuplicate(matchId: string): Promise<void> {
    // Store ignored duplicates in a separate collection if needed
    // For now, this is a no-op placeholder
  },
};
